import {
  Banknote,
  CalendarRange,
  Check,
  ChevronDown,
  Eye,
  Landmark,
  Loader,
  RefreshCcw,
  Search,
  Users,
  Wallet,
} from "lucide-react";

import { formatDate } from "../Utils/dateFormatter";
import TablePagination from "../Utils/TablePagination";

const SettlementTab = ({
  settlementType,

  settlementYear,
  setSettlementYear,
  currentYear,

  buildSettlementWeeks,

  settlementWeekSearch,
  setSettlementWeekSearch,

  showSettlementWeekDropdown,
  setShowSettlementWeekDropdown,

  selectedSettlementWeek,
  selectedSettlementWeekId,
  setSelectedSettlementWeekId,

  filteredSettlementWeekOptions,
  handleSettlementWeekSelect,

  search,
  setSearch,

  settlementLoading,
  fetchSettlementAmount,
  handleFetchSettlement,

  fromDate,
  toDate,

  totalSettlementAmount,
  pendingCount,
  settledCount,

  filteredSettlements,
  currentSettlements,

  currentPage,
  setCurrentPage,
  settlementPerPage,
  indexOfFirst,

  formatAmount,
  getStatusMeta,
  getDateRangeLabel,

  handleViewSettlement,
}) => {
  // =====================================================
  // MASK ACCOUNT NUMBER
  // =====================================================

  const maskAccountNo = (value) => {
    if (!value) return "-";

    const str = String(value);

    if (str.length <= 4) return str;

    return `${"•".repeat(Math.max(str.length - 4, 4))}${str.slice(-4)}`;
  };

  // =====================================================
  // PAGINATION
  // =====================================================

  const renderPagination = () => {
    const totalCount = filteredSettlements.length;

    if (totalCount <= settlementPerPage) return null;

    return (
      <TablePagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={settlementPerPage}
        onPageChange={setCurrentPage}
      />
    );
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-2">
      {/* =====================================================
          FILTER CARD
      ===================================================== */}

      <div className="rounded-3xl border border-orange-100 bg-white px-5 py-5 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-100">
              <CalendarRange size={14} />
              Weekly Settlement Filter
            </div>

            <p className="mt-3 text-lg font-bold text-gray-900">
              {settlementType || "Settlement Amount"}
            </p>

            <p className="mt-1 max-w-xl text-sm text-gray-500">
              Settlement amount is calculated week-wise. Search by week number
              or date range and apply the selected Monday to Sunday period.
            </p>
          </div>

          <div className="grid w-full grid-cols-1 gap-3 lg:w-auto lg:grid-cols-[130px_360px_280px_auto]">
            {/* =====================================================
                YEAR
            ===================================================== */}

            <div>
              <label
                htmlFor="settlement-year"
                className="mb-1 block text-xs font-semibold text-gray-500"
              >
                Year
              </label>

              <select
                id="settlement-year"
                value={settlementYear}
                onChange={(e) => {
                  const year = Number(e.target.value);

                  const weeks = buildSettlementWeeks(year);
                  const firstWeek = weeks[0];

                  setSettlementYear(year);
                  setSettlementWeekSearch("");
                  setShowSettlementWeekDropdown(false);
                  setCurrentPage(1);

                  if (firstWeek) {
                    setSelectedSettlementWeekId(firstWeek.id);

                    fetchSettlementAmount({
                      fromDate: firstWeek.fromDate,
                      toDate: firstWeek.toDate,
                    });
                  }
                }}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              >
                {Array.from({ length: 6 }).map((_, index) => {
                  const year = currentYear - index;

                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* =====================================================
                WEEK SELECTOR
            ===================================================== */}

            <div className="relative" data-settlement-week-picker>
              <label
                htmlFor="settlement-week-search"
                className="mb-1 block text-xs font-semibold text-gray-500"
              >
                Settlement Week
              </label>

              <div
                className="relative"
                role="combobox"
                aria-expanded={showSettlementWeekDropdown}
                aria-haspopup="listbox"
                aria-controls="settlement-week-listbox"
              >
                <Search
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  id="settlement-week-search"
                  type="text"
                  value={
                    showSettlementWeekDropdown
                      ? settlementWeekSearch
                      : selectedSettlementWeek?.label || ""
                  }
                  onFocus={() => {
                    setShowSettlementWeekDropdown(true);
                    setSettlementWeekSearch("");
                  }}
                  onChange={(e) => {
                    setSettlementWeekSearch(e.target.value);
                    setShowSettlementWeekDropdown(true);
                  }}
                  placeholder="Search week number or date..."
                  className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-10 text-sm font-semibold text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                />

                <button
                  type="button"
                  onClick={() => setShowSettlementWeekDropdown((prev) => !prev)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-400 transition hover:bg-orange-50 hover:text-orange-600"
                  aria-label="Toggle settlement week dropdown"
                >
                  <ChevronDown
                    size={18}
                    className={`transition ${
                      showSettlementWeekDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>

              {showSettlementWeekDropdown && (
                <div
                  id="settlement-week-listbox"
                  role="listbox"
                  className="absolute z-30 mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-orange-100 bg-white p-2 shadow-xl"
                >
                  {filteredSettlementWeekOptions.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      No week found
                    </div>
                  ) : (
                    filteredSettlementWeekOptions.map((week) => {
                      const isSelected = week.id === selectedSettlementWeek?.id;

                      return (
                        <button
                          key={week.id}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => handleSettlementWeekSelect(week)}
                          className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                            isSelected
                              ? "bg-orange-50 text-orange-700"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <div>
                            <div className="text-sm font-bold">
                              {week.shortLabel}
                            </div>

                            <div className="text-xs text-gray-500">
                              {week.display}
                            </div>
                          </div>

                          {isSelected && (
                            <span className="rounded-full bg-orange-500 p-1 text-white">
                              <Check size={13} />
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* =====================================================
                SEARCH RECORDS
            ===================================================== */}

            <div>
              <label
                htmlFor="settlement-table-search"
                className="mb-1 block text-xs font-semibold text-gray-500"
              >
                Search Records
              </label>

              <div className="relative">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  id="settlement-table-search"
                  type="text"
                  placeholder="Driver, bank, IFSC..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-11 w-full rounded-xl border border-gray-200 pl-9 pr-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                />
              </div>
            </div>

            {/* =====================================================
                APPLY BUTTON
            ===================================================== */}

            <button
              type="button"
              onClick={handleFetchSettlement}
              disabled={settlementLoading || !selectedSettlementWeek}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {settlementLoading ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <RefreshCcw size={16} />
              )}
              Apply Week
            </button>
          </div>
        </div>

        {/* =====================================================
            SELECTED WEEK SUMMARY
        ===================================================== */}

        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-orange-700">
              Selected Week
            </p>

            <p className="mt-1 text-base font-black text-gray-900">
              {selectedSettlementWeek?.shortLabel || "-"}
            </p>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
              From Date
            </p>

            <p className="mt-1 text-base font-black text-gray-900">
              {formatDate(fromDate)}
            </p>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
              To Date
            </p>

            <p className="mt-1 text-base font-black text-gray-900">
              {formatDate(toDate)}
            </p>
          </div>
        </div>
      </div>

      {/* =====================================================
          SUMMARY CARDS
      ===================================================== */}

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
        {/* TOTAL AMOUNT */}

        <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">
                Total Amount
              </p>

              <p className="mt-2 text-2xl font-black text-gray-900">
                {formatAmount(totalSettlementAmount)}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-3 text-orange-500 shadow-sm">
              <Wallet size={24} />
            </div>
          </div>
        </div>

        {/* DRIVERS */}

        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Drivers
              </p>

              <p className="mt-2 text-2xl font-black text-gray-900">
                {filteredSettlements.length}
              </p>
            </div>

            <div className="rounded-2xl bg-orange-50 p-3 text-orange-500">
              <Users size={24} />
            </div>
          </div>
        </div>

        {/* PENDING */}

        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Pending
              </p>

              <p className="mt-2 text-2xl font-black text-gray-900">
                {pendingCount}
              </p>
            </div>

            <div className="rounded-2xl bg-orange-50 p-3 text-orange-500">
              <Banknote size={24} />
            </div>
          </div>
        </div>

        {/* SETTLED */}

        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Settled
              </p>

              <p className="mt-2 text-2xl font-black text-gray-900">
                {settledCount}
              </p>
            </div>

            <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-600">
              <Banknote size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          SETTLEMENT TABLE
      ===================================================== */}

      <div className="mt-4 flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        {/* TABLE HEADER */}

        <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-3">
          <div>
            <p className="text-sm font-bold text-gray-900">
              Settlement Amount List
            </p>

            <p className="text-xs text-gray-500">
              Showing {currentSettlements.length} of{" "}
              {filteredSettlements.length} records
            </p>
          </div>

          <div className="hidden items-center gap-2 rounded-full bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 md:flex">
            <CalendarRange size={14} />
            {formatDate(fromDate)} to {formatDate(toDate)}
          </div>
        </div>

        {/* TABLE */}

        <div className="flex-1 overflow-y-auto max-h-[65vh]">
          <table className="min-w-[1100px] w-full text-left text-sm">
            <thead className="sticky top-0 z-20 bg-orange-500 text-white">
              <tr>
                <th className="px-4 py-3 text-center font-semibold">S/N</th>

                <th className="px-4 py-3 font-semibold">Driver</th>

                <th className="px-4 py-3 font-semibold">Amount</th>

                <th className="px-4 py-3 font-semibold">Bank Details</th>

                <th className="px-4 py-3 font-semibold">Account</th>

                <th className="px-4 py-3 font-semibold">IFSC</th>

                <th className="px-4 py-3 font-semibold">Status</th>

                <th className="px-4 py-3 font-semibold">Period</th>

                <th className="px-4 py-3 text-center font-semibold">Action</th>
              </tr>
            </thead>

            <tbody>
              {/* =================================================
                  LOADING
              ================================================= */}

              {settlementLoading ? (
                Array.from({ length: 7 }).map((_, index) => (
                  <tr key={index} className="border-b">
                    <td className="px-4 py-4 text-center">
                      <div className="mx-auto h-4 w-6 animate-pulse rounded bg-gray-200" />
                    </td>

                    <td className="px-4 py-4">
                      <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />

                      <div className="mt-2 h-3 w-20 animate-pulse rounded bg-gray-100" />
                    </td>

                    <td className="px-4 py-4">
                      <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                    </td>

                    <td className="px-4 py-4">
                      <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />

                      <div className="mt-2 h-3 w-28 animate-pulse rounded bg-gray-100" />
                    </td>

                    <td className="px-4 py-4">
                      <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
                    </td>

                    <td className="px-4 py-4">
                      <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
                    </td>

                    <td className="px-4 py-4">
                      <div className="h-6 w-20 animate-pulse rounded-full bg-gray-200" />
                    </td>

                    <td className="px-4 py-4">
                      <div className="h-4 w-36 animate-pulse rounded bg-gray-200" />
                    </td>

                    <td className="px-4 py-4 text-center">
                      <div className="mx-auto h-8 w-20 animate-pulse rounded-xl bg-gray-200" />
                    </td>
                  </tr>
                ))
              ) : currentSettlements.length === 0 ? (
                /* =================================================
                    EMPTY DATA
                ================================================= */

                <tr>
                  <td colSpan="9" className="px-4 py-14 text-center">
                    <div className="mx-auto flex max-w-sm flex-col items-center">
                      <div className="rounded-full bg-orange-50 p-4 text-orange-500">
                        <Banknote size={30} />
                      </div>

                      <p className="mt-3 text-base font-bold text-gray-900">
                        No settlement data found
                      </p>

                      <p className="mt-1 text-sm text-gray-500">
                        Try changing the week or search keyword.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                /* =================================================
                    DATA
                ================================================= */

                currentSettlements.map((item, index) => {
                  const statusMeta = getStatusMeta(item.status);

                  return (
                    <tr
                      key={`${item.driver_code}-${item.created_on}-${index}`}
                      className="border-b transition hover:bg-orange-50/40"
                    >
                      {/* SERIAL NUMBER */}

                      <td className="px-4 py-3 text-center text-gray-500">
                        {indexOfFirst + index + 1}
                      </td>

                      {/* DRIVER */}

                      <td className="px-4 py-3">
                        <div className="font-bold text-gray-900">
                          {item.name || "-"}
                        </div>

                        <div className="text-xs font-semibold text-orange-600">
                          {item.driver_code || "-"}
                        </div>
                      </td>

                      {/* AMOUNT */}

                      <td className="px-4 py-3">
                        <div className="inline-flex items-center gap-2 rounded-xl bg-orange-50 px-3 py-1.5 font-black text-orange-700">
                          <Banknote size={15} />

                          {formatAmount(item.total_amount)}
                        </div>
                      </td>

                      {/* BANK */}

                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <Landmark
                            size={17}
                            className="mt-0.5 shrink-0 text-orange-500"
                          />

                          <div>
                            <div className="font-semibold text-gray-900">
                              {item.bank_name || "-"}
                            </div>

                            <div className="text-xs text-gray-500">
                              {item.bank_holder_name || "-"}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* ACCOUNT */}

                      <td className="px-4 py-3 font-medium text-gray-700">
                        {maskAccountNo(item.account_no)}
                      </td>

                      {/* IFSC */}

                      <td className="px-4 py-3 font-mono text-xs font-semibold text-gray-700">
                        {item.ifsc_code || "-"}
                      </td>

                      {/* STATUS */}

                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${statusMeta.className}`}
                        >
                          {statusMeta.label}
                        </span>
                      </td>

                      {/* PERIOD */}

                      <td className="px-4 py-3 text-xs text-gray-600">
                        {getDateRangeLabel(item.created_on)}
                      </td>

                      {/* ACTION */}

                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewSettlement(item)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-orange-200 bg-white px-3 py-1.5 text-xs font-bold text-orange-700 transition hover:bg-orange-500 hover:text-white"
                          >
                            <Eye size={14} />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* =====================================================
          PAGINATION
      ===================================================== */}

      <div className="shrink-0 flex justify-center py-3">
        {renderPagination()}
      </div>
    </div>
  );
};

export default SettlementTab;
